/**
 * Ordinal search-demand ranking of public destinations, highest demand first.
 *
 * A bare ordered slug list, NOT a data table: the underlying search figures are
 * internal and must not enter this repo, so only the array order carries the
 * signal. Used purely to sort the public destinations index; a slug absent here
 * is "unmeasured", ranked after every measured city (see `UNRANKED_DEMAND`).
 *
 * Cities that sit outside the internal demand sample are deliberately omitted
 * rather than appended, so they sort as unmeasured instead of "least wanted".
 */
const DEMAND_RANKED_SLUGS: readonly string[] = [
  "rome",
  "london",
  "dubai",
  "barcelona",
  "amsterdam",
  "istanbul",
  "florence",
  "milan",
  "lisbon",
  "berlin",
  "venice",
  "madrid",
  "edinburgh",
  "seville",
  "naples",
  "athens",
  "porto",
  "malaga",
  "hamburg",
  "miami",
  "valencia",
  "munich",
  "cologne",
  "antalya",
  "alanya",
  "sintra",
  "dresden",
];

/**
 * Sort key for a destination we have no demand data for. Exported so callers
 * can ask "is this ranked?" rather than comparing against a magic number.
 */
export const UNRANKED_DEMAND = Number.MAX_SAFE_INTEGER;

/** Unmeasured cities are absent, so a lookup miss and "no demand data" coincide. */
const DEMAND_RANK = new Map(
  DEMAND_RANKED_SLUGS.map((slug, index) => [slug, index] as const)
);

/**
 * Ordinal demand position of a destination — lower means more in demand.
 * `UNRANKED_DEMAND` when we have no demand data for it.
 *
 * Deliberately an ORDINAL, never a volume. The underlying search figures are
 * internal and must not enter this repo; the array order already carries the
 * signal, and an ordinal cannot be reverse-engineered back into it.
 */
export function getDestinationDemandRank(slug: string): number {
  return DEMAND_RANK.get(slug) ?? UNRANKED_DEMAND;
}

/**
 * How deep every public destination report looks.
 *
 * Shared because the API caps the leaderboard at this depth and the web copy
 * has to name the same number. The platform ranks far more tours than this, so
 * a report's tour count is always "N of the top PUBLIC_LEADERBOARD_SIZE" and
 * never "the number of tours that rank here".
 */
export const PUBLIC_LEADERBOARD_SIZE = 20;

/**
 * List price of one city report, in cents. USD. Monthly only.
 *
 * A city report is its own product, not a credit-plan add-on. The product is
 * still a trial, so there is no annual price.
 *
 * `CITY_REPORT_PRICE_CENTS` is that monthly amount. Call sites that already
 * imported that name keep meaning "what a new purchase costs".
 *
 * Changing this constant does not rewrite an existing Stripe Price. Prices
 * are immutable, and a subscription item keeps the Price it was created with.
 * The sellable Price id stored on the city is rotated to the new amount;
 * the previous Price stays active so buyers already on it (the old $20/month)
 * keep that price and keep their entitlement.
 */
export const CITY_REPORT_MONTHLY_PRICE_CENTS = 3900;
export const CITY_REPORT_PRICE_CENTS = CITY_REPORT_MONTHLY_PRICE_CENTS;

/** Whole-dollar USD when the amount has no cents. */
export function formatUsdCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * The price a new buyer is offered. "$39/month".
 * Emails and pages share this so they cannot drift.
 */
export function cityReportPriceLabel(): string {
  return `${formatUsdCents(CITY_REPORT_PRICE_CENTS)}/month`;
}

/** Stripe caps a subscription at 20 items, and each city needs its own item. */
export const MAX_CITY_REPORTS_PER_ACCOUNT = 20;
